import React, { useState } from 'react';
import { Dialog, DialogTitle, DialogContent, DialogActions, Button, Box, Typography, TextField, Slider } from '@mui/material';

const TEXT_BOX_STYLES = [
  { id: 'border', label: 'Border', border: '2px solid #333', background: 'transparent' },
  { id: 'filled', label: 'Filled', border: '2px solid #333', background: '#f5f5f5' },
  { id: 'shadow', label: 'Shadow', border: 'none', background: '#ffffff', boxShadow: '0 4px 12px rgba(0,0,0,0.15)' },
  { id: 'colored', label: 'Colored', border: 'none', background: '#FFF9E6' },
];

const TEXT_BOX_COLORS = [
  { id: 'white', label: 'White', color: '#FFFFFF' },
  { id: 'light-gray', label: 'Light Gray', color: '#F5F5F5' },
  { id: 'cream', label: 'Cream', color: '#FFF9E6' },
  { id: 'light-blue', label: 'Light Blue', color: '#E3F2FD' },
  { id: 'light-green', label: 'Light Green', color: '#E8F5E9' },
  { id: 'light-yellow', label: 'Light Yellow', color: '#FFFDE7' },
  { id: 'light-pink', label: 'Light Pink', color: '#FCE4EC' },
  { id: 'light-purple', label: 'Light Purple', color: '#F3E5F5' },
];

export default function TextBoxPicker({ open, onClose, onInsert }) {
  const [selectedStyle, setSelectedStyle] = useState('border');
  const [selectedColor, setSelectedColor] = useState('white');
  const [boxWidth, setBoxWidth] = useState(400);
  const [padding, setPadding] = useState(16);
  const [placeholder, setPlaceholder] = useState('Type your text here...');

  const handleInsert = () => {
    const style = TEXT_BOX_STYLES.find(s => s.id === selectedStyle);
    const color = TEXT_BOX_COLORS.find(c => c.id === selectedColor);

    onInsert({
      style: selectedStyle,
      styleProps: style,
      backgroundColor: selectedStyle === 'filled' || selectedStyle === 'colored' ? color.color : (style.background === 'transparent' ? 'transparent' : style.background),
      width: boxWidth,
      padding: padding,
      placeholder: placeholder,
    });

    onClose();
  };

  const currentStyle = TEXT_BOX_STYLES.find(s => s.id === selectedStyle);
  const currentColor = TEXT_BOX_COLORS.find(c => c.id === selectedColor);

  // Use color for filled/colored styles, otherwise use style's default
  const previewBackground = (selectedStyle === 'filled' || selectedStyle === 'colored') 
    ? currentColor.color 
    : currentStyle.background;

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>Insert Text Box</DialogTitle>
      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 3, pt: 3 }}>
        
        {/* Preview */}
        <Box sx={{ border: '1px solid #eee', p: 3, borderRadius: 1, bgcolor: '#fafafa' }}>
          <Typography variant="caption" color="textSecondary" sx={{ display: 'block', mb: 2 }}>
            Preview:
          </Typography>
          <Box
            sx={{
              width: '100%',
              minHeight: '80px',
              padding: `${padding}px`,
              border: currentStyle.border,
              background: previewBackground,
              boxShadow: currentStyle.boxShadow || 'none',
              borderRadius: '4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: '#666',
              fontStyle: 'italic',
            }}
          >
            {placeholder}
          </Box>
        </Box>

        {/* Style Selection */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Text Box Style
          </Typography>
          <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(2, 1fr)', gap: 1 }}>
            {TEXT_BOX_STYLES.map(style => (
              <Button
                key={style.id}
                variant={selectedStyle === style.id ? 'contained' : 'outlined'}
                onClick={() => setSelectedStyle(style.id)}
                sx={{
                  background: selectedStyle === style.id ? '#D4AF37' : 'transparent',
                  color: selectedStyle === style.id ? '#1A1A1A' : '#333',
                  fontWeight: selectedStyle === style.id ? 600 : 400,
                  borderColor: '#ddd',
                  '&:hover': {
                    background: selectedStyle === style.id ? '#F5D76E' : '#f5f5f5',
                  }
                }}
              >
                {style.label}
              </Button>
            ))}
          </Box>
        </Box>

        {/* Color Selection - Only show for filled/colored styles */}
        {(selectedStyle === 'filled' || selectedStyle === 'colored') && (
          <Box>
            <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
              Background Color
            </Typography>
            <Box sx={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 1.5 }}>
              {TEXT_BOX_COLORS.map(color => (
                <Box
                  key={color.id}
                  onClick={() => setSelectedColor(color.id)}
                  sx={{
                    width: '100%',
                    aspectRatio: '1',
                    backgroundColor: color.color,
                    border: selectedColor === color.id ? '3px solid #D4AF37' : '2px solid #ddd',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    boxShadow: selectedColor === color.id ? '0 4px 8px rgba(212, 175, 55, 0.3)' : 'none',
                    '&:hover': {
                      transform: 'scale(1.1)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                    }
                  }}
                  title={color.label}
                />
              ))}
            </Box>
          </Box>
        )}

        {/* Width Slider */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Width: {boxWidth}px
          </Typography>
          <Slider
            value={boxWidth}
            onChange={(e, val) => setBoxWidth(val)}
            min={200}
            max={800}
            step={20}
            marks={[
              { value: 200, label: '200' },
              { value: 500, label: '500' },
              { value: 800, label: '800' },
            ]}
            sx={{
              '& .MuiSlider-thumb': { backgroundColor: '#D4AF37' },
              '& .MuiSlider-track': { backgroundColor: '#D4AF37' },
              '& .MuiSlider-rail': { backgroundColor: '#ddd' },
            }}
          />
        </Box>

        {/* Padding Slider */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Padding: {padding}px
          </Typography>
          <Slider
            value={padding}
            onChange={(e, val) => setPadding(val)}
            min={8}
            max={48}
            step={4}
            marks={[
              { value: 8, label: '8' },
              { value: 24, label: '24' },
              { value: 48, label: '48' },
            ]}
            sx={{
              '& .MuiSlider-thumb': { backgroundColor: '#D4AF37' },
              '& .MuiSlider-track': { backgroundColor: '#D4AF37' },
              '& .MuiSlider-rail': { backgroundColor: '#ddd' },
            }}
          />
        </Box>

        {/* Placeholder Text */}
        <Box>
          <Typography variant="subtitle2" sx={{ mb: 1.5, fontWeight: 600 }}>
            Placeholder Text
          </Typography>
          <TextField
            fullWidth
            value={placeholder}
            onChange={(e) => setPlaceholder(e.target.value)}
            placeholder="Enter placeholder text..."
            variant="outlined"
            size="small"
          />
        </Box>

      </DialogContent>
      <DialogActions sx={{ gap: 1, p: 2 }}>
        <Button onClick={onClose} variant="outlined">
          Cancel
        </Button>
        <Button onClick={handleInsert} variant="contained" sx={{ background: '#D4AF37', color: '#1A1A1A' }}>
          Insert Text Box
        </Button>
      </DialogActions>
    </Dialog>
  );
}
